import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ActivityIndicator } from '../../../../components'
import ChallengeInfoModal from '../../../../components/challenge-info-modal'
import Button from '../../../../components/tailwind/Button'
import { Heading } from '../../../../components/ui/Heading'
import { createAnalyseInput } from '../../../../constants/career-analyse'
import {
  useLanguageContext,
  usePartnerContext,
  useUserContext
} from '../../../../context'
import { mutation, query } from '../../../../graphql'
import { DIAGNOSTIC_VERSION } from '../../../../helpers'
import { getCreateCareerInput } from '../../../../helpers/career'
import { mapChallengesToCoveredDimensions } from '../../../../helpers/diagnostic'
import { useSystemQuery } from '../../../../hooks/use-system-query'
import { useCareerPresetQuery } from '../../../../hooks/useCareerPresetQuery'
import {
  LIFE_CYCLE_STAGES,
  useLifecycleStageTracking
} from '../../../../hooks/useLifecycleStageTracking'
import { useLimitGuards } from '../../../pricing/guards'
import { invalidatePricingUsage } from '../../../pricing/hooks'
import { LIMIT_DIALOG_ACTIONS } from '../../../pricing/pricing-dialog-context'
import { useDiscover } from '../../hooks/useDiscover'
import { useSelectionParams } from '../../hooks/useSelectionParams'
import {
  DEFAULT_CULTURAL_FIT_STATE,
  DEFAULT_DIMENSIONS,
  MESSAGE_ROLES,
  MESSAGE_TYPES,
  sendSmartMessageSequence
} from '../../utils'
import ChallengeCard from '../ChallengeCard'
import DurationSlider from './DurationSlider'

const Container = ({ children, className }) => (
  <div
    className={clsx(
      'rounded-2xl border border-softGrey bg-white p-6',
      className
    )}
  >
    {children}
  </div>
)

Container.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
}

const MANDATORY_CHALLENGES = [
  'BIG_FIVE',
  'HEROS_JOURNEY',
  'TOWER_OF_LONDON',
  'FLANKER',
  'RIASEC_IMAGES'
]

export default function Step2Configure({ example, onBack }) {
  const { t } = useTranslation()
  const {
    enableAssistant,
    sendMessageToAssistant,
    setAssistantIsTyping,
    setAssistantFocus
  } = useDiscover()
  const { cognitoUser } = useUserContext()
  const { data: system, isLoading } = useSystemQuery({
    username: cognitoUser?.username
  })
  const [openInfoModal, setOpenInfoModal] = useState(false)
  const [challenge, setChallenge] = useState(null)
  const [isLoadingCreate, setIsLoadingCreate] = useState(false)
  const hasShownAssistantRecommendation = useRef(false)

  const { trackLifecycleStage } = useLifecycleStageTracking()

  const { addCareer } = useSelectionParams()

  const { language } = useLanguageContext()
  const { partner } = usePartnerContext()

  const queryClient = useQueryClient()
  const { guardCareerActivation } = useLimitGuards()

  const { data: preset, isLoading: isLoadingPreset } = useCareerPresetQuery(
    example.id
  )

  const presetName = preset?.name[language]

  const currentChallengeConfigId = system?.currentChallengeConfigId

  const objectDimensions = system.dimensions
  delete objectDimensions.createdAt
  delete objectDimensions.updatedAt
  delete objectDimensions.type
  delete objectDimensions.data
  delete objectDimensions.id

  const trackOnboardingCareerCreated = () => {
    const storageKey = 'onboarding-career-created'
    if (localStorage.getItem(storageKey) === 'true') return

    trackLifecycleStage({
      lifecycle_stage: LIFE_CYCLE_STAGES.ONBOARDING_CAREER_CREATED,
      lifecycle_stage_before: LIFE_CYCLE_STAGES.ONBOARDING_STARTED,
      aivy_form_id: partner.aivy_form_id,
      lead_progress: (2 / 5).toFixed(2),
      partner_id: partner.id
    })
    localStorage.setItem(storageKey, 'true')
  }

  const { challenges, MIN, MAX, availableSteps } = useMemo(() => {
    if (!system?.challenges?.data)
      return { challenges: [], MIN: 6, MAX: 30, availableSteps: [] }

    const challengeList = Object.values(JSON.parse(system.challenges.data))
    const activeChallenges = challengeList.filter(({ active }) => active)

    const mandatoryChallenges = activeChallenges.filter((challenge) =>
      MANDATORY_CHALLENGES.includes(challenge.exam_id)
    )

    const optionalChallenges = activeChallenges.filter(
      (challenge) => !MANDATORY_CHALLENGES.includes(challenge.exam_id)
    )

    const mandatoryTime = mandatoryChallenges.reduce(
      (sum, challenge) => sum + challenge.timeToComplete / 1000 / 60,
      0
    )

    const sortedOptionalChallenges = optionalChallenges.sort(
      (a, b) => a.timeToComplete / 1000 / 60 - b.timeToComplete / 1000 / 60
    )

    const steps = [Math.ceil(mandatoryTime)]
    let currentTime = mandatoryTime

    for (const challenge of sortedOptionalChallenges) {
      const challengeDuration = challenge.timeToComplete / 1000 / 60
      currentTime += challengeDuration
      steps.push(Math.ceil(currentTime))
    }

    const minTime = Math.max(mandatoryTime, 6)
    const maxTime = currentTime

    return {
      challenges: challengeList,
      MIN: Math.ceil(minTime),
      MAX: Math.ceil(maxTime),
      availableSteps: steps
    }
  }, [system?.challenges?.data])

  const [minutes, setMinutes] = useState(15)
  // Ref für vorherigen Minutenwert
  const prevMinutesRef = useRef(minutes)
  // Ref, um zu tracken, ob die Message schon gesendet wurde
  const hasPushedSliderMessage = useRef(false)
  const hasPushedSliderDecreaseMessage = useRef(false)
  // Custom Setter für Minuten, um Slider-Message zu pushen
  const handleMinutesChange = (newMinutes) => {
    // Prüfen, ob der Wert erhöht wurde und die Message noch nicht gesendet wurde
    if (
      newMinutes > prevMinutesRef.current &&
      !hasPushedSliderMessage.current
    ) {
      // Assistant Message pushen
      enableAssistant()
      sendMessageToAssistant({
        id: 'slider-info',
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT,
        title: t('discover.assistant.create_career.slider.title'),
        description: t('discover.assistant.create_career.slider.description')
      })

      setTimeout(() => {
        setAssistantFocus(false)
      }, 1500)
      hasPushedSliderMessage.current = true
    } else if (
      newMinutes < prevMinutesRef.current &&
      !hasPushedSliderDecreaseMessage.current
    ) {
      // Assistant Message pushen (Verringerung)
      enableAssistant()
      sendMessageToAssistant({
        id: 'slider-info-decrease',
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT,
        title: t('discover.assistant.create_career.slider.decrease.title'),
        description: t(
          'discover.assistant.create_career.slider.decrease.description'
        )
      })

      setTimeout(() => {
        setAssistantFocus(false)
      }, 1500)
      hasPushedSliderDecreaseMessage.current = true
    }
    prevMinutesRef.current = newMinutes
    setMinutes(newMinutes)
  }

  useEffect(() => {
    if (availableSteps.length > 0 && !availableSteps.includes(minutes)) {
      const closest = availableSteps.reduce((prev, curr) =>
        Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev
      )
      setMinutes(closest)
    }
  }, [availableSteps, minutes])

  const handleChallengeInfo = useCallback((selectedChallenge) => {
    setChallenge(selectedChallenge)
    setOpenInfoModal(true)
  }, [])

  const selectedChallenges = useMemo(() => {
    const activeChallenges = challenges.filter(({ active }) => active)

    const mandatoryChallenges = activeChallenges.filter((challenge) =>
      MANDATORY_CHALLENGES.includes(challenge.exam_id)
    )

    const optionalChallenges = activeChallenges.filter(
      (challenge) => !MANDATORY_CHALLENGES.includes(challenge.exam_id)
    )

    const mandatoryTime = mandatoryChallenges.reduce(
      (sum, challenge) => sum + challenge.timeToComplete / 1000 / 60,
      0
    )

    const sortedOptionalChallenges = optionalChallenges.sort(
      (a, b) => a.timeToComplete / 1000 / 60 - b.timeToComplete / 1000 / 60
    )

    const selectedOptional = []
    let totalTime = mandatoryTime

    for (const challenge of sortedOptionalChallenges) {
      const challengeDuration = challenge.timeToComplete / 1000 / 60
      if (totalTime + challengeDuration <= minutes) {
        selectedOptional.push(challenge)
        totalTime += challengeDuration
      }
    }

    return [...mandatoryChallenges, ...selectedOptional]
  }, [challenges, minutes])

  useEffect(() => {
    if (
      preset &&
      selectedChallenges.length > 0 &&
      !isLoadingPreset &&
      !hasShownAssistantRecommendation.current
    ) {
      // Trigger assistant message when preset is loaded and challenges are selected
      const timer = setTimeout(() => {
        showAssistantRecommendation()
        hasShownAssistantRecommendation.current = true
      }, 1000) // Small delay for better UX

      return () => clearTimeout(timer)
    }
    // Absichtlich ohne showAssistantRecommendation, die Funktion entsteht bei
    // jedem Render neu und würde den Timer sonst ständig neu starten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, selectedChallenges.length, isLoadingPreset])

  const showAssistantRecommendation = () => {
    enableAssistant()

    const getRecommendationMessage = () => {
      return t('discover.create_career.step_2.assistant.recommendation', {
        career: presetName || example.title,
        minutes,
        challengeCount: selectedChallenges.length
      })
    }

    const getFollowUpMessage = () => {
      return t('discover.create_career.step_2.assistant.followUp')
    }

    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 1,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT,
              title: t('discover.create_career.step_2.assistant.title'),
              description: getRecommendationMessage()
            }),
          textLength: getRecommendationMessage().length + 50
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 2,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT,
              description: getFollowUpMessage()
            }),
          textLength: getFollowUpMessage().length
        }
      ],
      callbackFn: () => {
        setAssistantFocus(false)
      }
    })
  }

  const STEP = 1

  if (isLoading || !system) {
    return <div className='pt-4'>{t('common.loading')}</div>
  }

  const clickContinue = async () => {
    setIsLoadingCreate(true)
    try {
      // Vor createCareer prüfen. Ist kein Platz frei, öffnet der Guard den
      // Limit-Dialog, und es bleibt keine verwaiste WAIT_ACTIVE-Stelle zurück.
      // Discover-Vorlagen mit is_demo zählen wie jede aktive Stelle.
      const allowed = await guardCareerActivation(
        { partner_id: partner?.id, status: 'WAIT_ACTIVE', is_demo: true },
        {
          source: 'discover_create_career',
          action: LIMIT_DIALOG_ACTIONS.DISCOVER
        }
      )
      if (!allowed) return

      // 1. Erstelle die Karriere mit Status WAIT_ACTIVE statt ACTIVE
      const input = {
        external_custom_id: undefined,
        diagnostic_version: DIAGNOSTIC_VERSION,
        challenge_config_id: currentChallengeConfigId,
        app_settings: {
          cultural_fit: JSON.stringify({ ...DEFAULT_CULTURAL_FIT_STATE }),
          challenges: JSON.stringify(
            selectedChallenges.map((c) => ({ exam_id: c.exam_id }))
          )
        },
        // Vorlagenstelle aus Discover, zählt als Beispieldaten. Das Top-Level-Flag
        // ist der gemeinsame Marker (helpers/sample-data.js), die Meta bleibt.
        is_demo: true,
        customer_dashboard_meta: JSON.stringify({
          is_demo: true,
          expert_mode_used: false
        }),
        ...getCreateCareerInput({
          title: example.title,
          category: '-1', // PRESET
          language,
          partner_id: partner?.id,
          status: 'WAIT_ACTIVE' // Änderung von ACTIVE zu WAIT_ACTIVE
        })
      }

      const new_career = await mutation({
        mutation: 'createCareer',
        input
      })

      // Berechne die Dimensionen aus den ausgewählten Challenges
      const selectedChallengesWithExamId = selectedChallenges.map(
        (challenge) => ({
          exam_id: challenge.exam_id
        })
      )

      // Hole die Dimensionen aus dem System
      // Verwende system.dimensions_pdf_reports.data statt system.dimensions
      const system_dimensions_data = JSON.parse(
        system.dimensions_pdf_reports.data
      )

      // Berechne die Dimensionen für die ausgewählten Challenges
      let dimensions = mapChallengesToCoveredDimensions({
        challenges: selectedChallengesWithExamId,
        dimensions: Object.values(system_dimensions_data)
      })

      // Wenn dimensions leer ist, verwende DEFAULT_DIMENSIONS
      if (!dimensions || dimensions.length === 0) {
        dimensions = DEFAULT_DIMENSIONS
      }

      // 2. Erstelle die Analyse aus der Vorlage
      const analyseInput = createAnalyseInput({
        career_id: new_career.id,
        status: 'COMPLETED',
        partner_id: partner?.id,
        username: cognitoUser?.username
      })

      // Explizit Berechtigungen setzen
      analyseInput.authorized_create = ['__create_' + partner?.id]
      analyseInput.authorized_read = ['__read_low_' + partner?.id]
      analyseInput.authorized_update = ['__update_' + partner?.id]
      analyseInput.authorized_delete = ['__delete_' + partner?.id]

      analyseInput.verified = true

      // Füge die berechneten Dimensionen zum Preset hinzu
      const presetData = preset?.career_analyse
        ? JSON.parse(preset.career_analyse)
        : {}

      // Throw error if presetData is empty object
      if (
        presetData &&
        typeof presetData === 'object' &&
        Object.keys(presetData).length === 0
      ) {
        throw new Error('Preset data is empty. Cannot proceed.')
      }

      // Stelle sicher, dass override_defaults existiert
      if (!presetData.override_defaults) {
        presetData.override_defaults = {}
      }

      // Füge die Dimensionen hinzu
      presetData.override_defaults.dimensions = dimensions
      presetData.override_defaults.selected_challenges =
        selectedChallengesWithExamId

      // Aktualisiere die Analyse-Eingabe
      analyseInput.input = JSON.stringify(presetData)

      // Füge Preset-Metadaten hinzu
      const presetCopy = { ...preset }
      delete presetCopy.career_analyse
      delete presetCopy.displayValue
      delete presetCopy.synonym
      delete presetCopy.syns

      // Stelle sicher, dass die name-Eigenschaft korrekt formatiert ist
      if (!presetCopy.name || typeof presetCopy.name !== 'object') {
        presetCopy.name = {
          de: example.title,
          en: example.title
        }
      }

      analyseInput.preset = JSON.stringify(presetCopy)

      await mutation({
        mutation: 'createCareerAnalyse',
        input: analyseInput
      })

      // Aktualisiere die Karriere mit den berechneten Dimensionen
      await mutation({
        mutation: 'updateCareer',
        input: {
          id: new_career.id,
          career_analyse: JSON.stringify({
            ...presetData,
            override_defaults: {
              ...presetData.override_defaults,
              dimensions,
              selected_challenges: selectedChallengesWithExamId,
              selection_difficulty: 'NORMAL'
            }
          })
        }
      })

      // Kurze Verzögerung, um sicherzustellen, dass die Daten gespeichert sind
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // 3. Rufe synthesizeAnalyze auf, um die Matching-Daten zu generieren
      await query({
        query: 'synthesizeAnalyze',
        variables: { career_id: new_career.id }
      })

      // Kurze Verzögerung, um sicherzustellen, dass die Synthese abgeschlossen ist
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // 4. Aktiviere die Karriere nach der Synthese
      await mutation({
        mutation: 'updateCareer',
        input: {
          id: new_career.id,
          status: 'ACTIVE'
        }
      })

      // Die Stelle belegt jetzt einen Platz, Banner und Meter zählen neu
      invalidatePricingUsage({
        queryClient,
        partnerId: partner?.id,
        resource: 'careers'
      })

      // 5. Zeige finale Assistenten-Nachricht
      enableAssistant()

      const getFinalMessage = () => {
        return t('discover.create_career.step_2.assistant.final_message')
      }

      const getCtaMessage = () => {
        return t('discover.create_career.step_2.assistant.cta_message')
      }

      sendSmartMessageSequence({
        setIsTyping: setAssistantIsTyping,
        messages: [
          {
            fn: () =>
              sendMessageToAssistant({
                id: 3,
                role: MESSAGE_ROLES.ASSISTANT,
                type: MESSAGE_TYPES.TEXT,
                title: t('discover.create_career.step_2.assistant.final_title'),
                description: getFinalMessage()
              }),
            textLength: getFinalMessage().length
          },
          {
            fn: () =>
              sendMessageToAssistant({
                id: 4,
                role: MESSAGE_ROLES.ASSISTANT,
                type: MESSAGE_TYPES.CTA,
                description: getCtaMessage(),
                cta: {
                  text: t('discover.create_career.step_2.assistant.cta_button'),
                  action: () => {
                    // 6. Navigiere zur Match-Seite nach CTA-Klick
                    trackOnboardingCareerCreated()
                    addCareer(new_career.id, {
                      showAssistant: true,
                      careerCreated: true
                    })
                  }
                }
              }),
            textLength: getCtaMessage().length
          }
        ]
      })
    } catch (error) {
      console.error('Error creating career:', error)
    } finally {
      setIsLoadingCreate(false)
    }
  }

  return (
    <div className='pt-4'>
      {/* <StepHeadline>
        {t('discover.create_career.step_2.title', { career: example.title })}
      </StepHeadline>
      <StepDescription>
        {t('discover.create_career.step_2.description', {
          career: presetName || example.title
        })}
      </StepDescription> */}
      <Container className={'min-h-52'}>
        <Heading level={6} className={'text-primaryBlue'}>
          {t('discover.create_career.step_2.testDurationLabel')}
        </Heading>
        <div className='mt-10 w-full'>
          <div className='flex flex-col items-center justify-center'>
            {minutes === 8 && (
              <div className='mb-2 flex h-[26px] items-center justify-center rounded-full border border-[#BCE7CB] bg-[#DCFCE7] px-2.5 text-xs font-semibold text-darkGreen'>
                {t('common.recommended')}
              </div>
            )}
            <Heading level={2}>
              {t('discover.create_career.step_2.testDurationMinutes', {
                minutes
              })}
            </Heading>
          </div>
          {isLoadingPreset ? (
            <ActivityIndicator className='flex items-center justify-center py-5' />
          ) : (
            <div className='mx-auto mt-4 flex h-11 w-full max-w-[40rem] flex-row items-center justify-between rounded-full border border-softGrey px-6'>
              <span className='w-32 text-right text-xs text-rockGrey'>
                <span className='font-semibold'>
                  {t('discover.create_career.step_2.effort_for_talent')}
                </span>
                <br />
                <span className='font-normal'>
                  {t('discover.create_career.step_2.low')}
                </span>
              </span>
              <div className='flex w-full max-w-60 flex-col items-center px-4'>
                <DurationSlider
                  min={MIN}
                  max={MAX}
                  step={STEP}
                  value={minutes}
                  onChange={handleMinutesChange}
                  availableSteps={availableSteps}
                />
              </div>

              <span className='w-32 text-left text-xs text-rockGrey'>
                <span className='font-semibold'>
                  {' '}
                  {t('discover.create_career.step_2.talent_profile_details')}
                </span>
                <br />
                <span className='font-normal'>
                  {t('discover.create_career.step_2.high')}
                </span>
              </span>
            </div>
          )}
        </div>
      </Container>
      <Container className={'mt-6'}>
        <Heading level={3}>
          {t('discover.create_career.step_2.includedTasks', {
            count: selectedChallenges.length
          })}
        </Heading>
        <div className='mt-6'>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {selectedChallenges.map((challenge) => (
              <ChallengeCard
                key={challenge.exam_id}
                challenge={challenge}
                onClickMoreInformation={() => handleChallengeInfo(challenge)}
              />
            ))}
          </div>
        </div>
      </Container>
      <div className='mt-6 flex flex-row items-center gap-x-2.5'>
        <Button.SecondaryXL text={t('common.back')} onClick={onBack} />
        <Button.PrimaryXL
          text={t('common.next')}
          onClick={clickContinue}
          isLoading={isLoadingPreset || isLoadingCreate}
          // onClick={() => onNext(selectedCareer)}
          // disabled={!selectedCareer}
        />
      </div>
      <ChallengeInfoModal
        open={openInfoModal}
        setOpen={setOpenInfoModal}
        challenge={challenge}
      />
    </div>
  )
}

Step2Configure.propTypes = {
  example: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string
  }).isRequired,
  onBack: PropTypes.func
}
